/**
 * The visual half of a session: whatever the guide last drew.
 *
 * The old four-mode map is gone (DECISIONS #62). Diagrams arrive as
 * `diagram` frames — one per `show_diagram` tool call — and this pane shows
 * the newest, with a strip of title chips to walk back through the earlier
 * ones. Nothing here is pre-generated: an empty pane means the conversation
 * has not needed a picture yet, which is an honest state, not a hole.
 *
 * Rendering degrades in one step: MermaidDiagram already falls back to the
 * diagram source when mermaid cannot parse it (cheap models do emit broken
 * syntax); the pane adds the one line of explanation the transcript's inline
 * cards do not need.
 *
 * Round 4 made the picture readable rather than only viewable. The title is
 * the pane's, and the pane's alone (the transcript card and the tool chip both
 * stopped repeating it). The named nodes and edges are clickable, and what
 * they say docks along the bottom edge, where the drawing re-centres above it
 * instead of disappearing behind it (PartPanel explains the choice).
 *
 * Round 6 widens that from one part to a GROUP (owner: "I want to select any
 * group of nodes and edges in the diagram and ask for more/less detail on
 * them. The diagram and the answer would keep the rest the same and only
 * modify on the selected components."). Shift picks: shift-click a part,
 * shift-Enter on one, or shift-drag a band across several. What comes back is
 * a question staged into the composer that names the picked parts and asks for
 * them, and only them, to move (selection.ts writes it).
 *
 * Round 8 (DECISIONS #71) closes that ask over the wire. The picked labels and
 * the mermaid on screen ride the send as `focus` (api/sse.ts), so the redraw
 * starts from the drawing the reader is looking at rather than from the
 * model's memory of it. The wire still carries a drift note and change marks
 * for a redraw that moved more than was asked; the pane no longer prints
 * them (2026-09-06; owner: "There's no need for the What changed here and
 * this redraw stuff"). The dock says what the answer said about a part
 * instead (answerQuotes).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import type { Ulid } from '../../api/types';
import {
  useChatStore,
  useSessionDiagrams,
  useSessionLive,
  type PaneView,
} from '../../stores/chatStore';
import { useParams } from 'react-router';

import { MermaidDiagram } from '../chat/MermaidDiagram';
import { useProduct } from '../../api/queries';
import { ErrorBoundary, errorMessageOf } from '../../lib/errorBoundary';
import { useRuntimeRepo } from '../liveinfra/api';
import { useLiveOverlay } from '../liveinfra/api';
import { LiveEnvSwitcher, LiveMapCallout } from '../liveinfra/LiveMapControls';
import { answerQuotes } from './answerQuotes';
import { RuntimeMapView } from './RuntimeMapView';
// The selection, change-mark and edge-line styles. Their import left with
// the per-turn diagram strip in the 2026-09-06 round and nothing else
// carried it, so every rule in the file (the dashed pick outline, the
// marquee wash, the what-changed marks) had been silently absent from the
// page since (audit 2026-09-07). The pane is the one place both drawings
// mount, so it owns the import.
import './selection.css';
import type { MentionCandidate } from '../chat/mentions';
import { newestAnswer, type DiagramPart, type SessionDiagram } from '../chat/transcript';
import { DiagramView } from './DiagramView';
import {
  focusPart,
  markSelectedParts,
  partLabelAt,
  partsInRect,
  type ScreenRect,
} from './partHotspots';
import { PartPanel } from './PartPanel';
import {
  orderedSelection,
  selectionCountLabel,
  toggleSelection,
} from './selection';

/** Said once, in the corner, to whoever has not tried it yet. Round 9 cut
 * it down (owner: "that instruction is too long") and stopped hiding that
 * cmd works too: both modifiers always did, so the hint says the short
 * truth and the drag stays discoverable. */
const SELECT_HINT = 'Click a part';

/** A pointer, for the hint that says the drawing is clickable. */
function PointerIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="shrink-0"
    >
      <path d="M4 3l7.5 17 2.5-7 7-2.5z" />
    </svg>
  );
}

/**
 * The strip's second view (DECISIONS #86): the runtime map, drawn from data
 * for the codebase this product reads, with the environment switcher on it.
 * Present whenever the codebase declares a deploy manifest.
 */
const RUNTIME_TAB_TITLE =
  "The product's infrastructure: services, queues and schedules per environment. Not a drawing of this conversation";

/** One of the strip's two views (DECISIONS #96): the board, or the runtime map. */
interface StripTab {
  id: PaneView;
  label: string;
  title: string;
  testId?: string;
}

/**
 * The one strip over the pane (DECISIONS #94): the two views as one
 * segmented switch (2026-09-05; owner: "should it be a switch?"), and the
 * active view's controls at the far end of the same row.
 */
function DiagramStrip({
  tabs,
  active,
  onPick,
  trailing,
}: {
  tabs: readonly StripTab[];
  active: PaneView;
  onPick: (id: PaneView) => void;
  /** the active view's controls, at the far end of the same row */
  trailing?: ReactNode;
}) {
  return (
    <div
      data-testid="diagram-strip"
      className="flex shrink-0 items-center gap-2 border-b border-default px-3 py-2"
    >
      <div role="tablist" aria-label="The two views of this conversation" className="lm-segmented">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === active}
            title={tab.title}
            data-testid={tab.testId}
            onClick={() => onPick(tab.id)}
            className="lm-segmented-option"
          >
            {tab.label}
          </button>
        ))}
      </div>
      {trailing !== undefined ? (
        <div className="ml-auto flex shrink-0 items-center gap-2 pl-2">{trailing}</div>
      ) : null}
    </div>
  );
}

/**
 * The infrastructure view's controls, on the strip beside its tab
 * (2026-09-05; owner: "this can be designed better"): one row over the
 * drawing, not a second header under the first.
 */
function RuntimeStripControls({ repoId }: { repoId: Ulid }) {
  const overlay = useLiveOverlay(repoId);
  return (
    <>
      <LiveMapCallout repoId={repoId} />
      <LiveEnvSwitcher repoId={repoId} overlay={overlay.data ?? null} />
    </>
  );
}

/** `a/b/c.py:12` → `a/b`; a bare file name has no dir and yields null. */
function dirOfReceipt(receipt: string): string | null {
  const path = receipt.split(':')[0] ?? '';
  const at = path.lastIndexOf('/');
  return at > 0 ? path.slice(0, at) : null;
}

/** How many cited files ride the map request; a board cites a few dozen at most. */
const SCOPE_FILES_MAX = 40;

/** `a/b/c.py:12` → `a/b/c.py`; a receipt with no path yields null. */
function fileOfReceipt(receipt: string): string | null {
  const path = receipt.split(':')[0] ?? '';
  return path.length > 0 ? path : null;
}

/** The dirs, services and files a drawing touches, or undefined when it names none. */
export function runtimeScopeOf(
  diagram: SessionDiagram | null,
): { dirs: string[]; services: string[]; files: string[]; entities: string[]; question: string } | undefined {
  if (diagram === null) return undefined;
  const services = [...new Set(diagram.parts.map((p) => p.runs_on).filter((s): s is string => typeof s === 'string' && s.length > 0))];
  // The runtime entities the parts named outright (2026-09-07): the server
  // cuts the Infrastructure view to exactly these when there are any.
  const entities = [...new Set(diagram.parts.flatMap((p) => p.runtime))];
  const receipts = diagram.parts.flatMap((p) => p.evidence);
  const dirs = [...new Set(receipts.map(dirOfReceipt).filter((d): d is string => d !== null))];
  // The files the answer cited (2026-09-06): each part's code facts narrow to
  // them, so a service's dock shows the routes this question is about.
  const files = [...new Set(receipts.map(fileOfReceipt).filter((f): f is string => f !== null))].slice(0, SCOPE_FILES_MAX);
  if (services.length === 0 && dirs.length === 0 && entities.length === 0) return undefined;
  return { dirs, services, files, entities, question: diagram.title };
}

export function DiagramPane({ sessionId }: { sessionId: Ulid }) {
  const diagrams = useSessionDiagrams(sessionId);
  // The runtime map (DECISIONS #86) belongs to the product's codebase, not
  // to a turn: the first codebase with a deploy manifest is the one drawn.
  const { pid = '' } = useParams<'pid'>();
  const product = useProduct(pid.length > 0 ? pid : undefined);
  const runtimeRepo = useRuntimeRepo(product.data?.repos ?? []);
  // Round 9 (owner: "when the chat is answering, there should be some
  // indication that diagram is in progress too"): the pane knows a turn is
  // running and says so instead of claiming "no diagram yet" mid-answer.
  const streaming = useSessionLive(sessionId).streaming;
  const paneView = useChatStore((s) => s.paneView);
  const setPaneView = useChatStore((s) => s.setPaneView);
  // Two views (DECISIONS #96; owner: "just product view and infra view with
  // a cleaner design"): the board, and the product as it runs. The store
  // holds which is up (`paneView`); the runtime view also needs a codebase
  // with a runtime to draw, and falls back to the board without one.
  const showRuntime = runtimeRepo !== null && paneView === 'runtime';

  // The board is the conversation's ONE drawing: the newest picture drawn,
  // edited in place by every answer. Earlier versions stay in the
  // transcript's history and nowhere else.
  const active = diagrams.at(-1) ?? null;
  // The infrastructure slice this conversation is about (DECISIONS #88;
  // owner: "Infra view should also be specific to the question"): the
  // services the answer's parts run on, and the code dirs its receipts
  // cite. The server cuts the map to them and one hop around them.
  const runtimeScope = useMemo(() => runtimeScopeOf(active), [active]);

  const tabs: StripTab[] = [
    {
      id: 'board',
      label: 'Product',
      title: 'The board: the drawing this conversation has built up, edited by every answer',
      testId: 'pane-tab-product',
    },
    ...(runtimeRepo !== null
      ? [
          {
            id: 'runtime' as const,
            label: 'Infrastructure',
            title: RUNTIME_TAB_TITLE,
            testId: 'pane-tab-runtime',
          },
        ]
      : []),
  ];
  // The strip exists once there is a second view to switch to.
  const strip =
    runtimeRepo !== null ? (
      <DiagramStrip
        tabs={tabs}
        active={showRuntime ? 'runtime' : 'board'}
        onPick={setPaneView}
        trailing={showRuntime ? <RuntimeStripControls repoId={runtimeRepo.id} /> : undefined}
      />
    ) : null;

  if (showRuntime && runtimeRepo !== null) {
    return (
      <div data-testid="diagram-pane" data-state="runtime" className="flex h-full min-h-0 flex-col">
        {strip}
        <RuntimeMapView
          repoId={runtimeRepo.id}
          scope={runtimeScope}
          title={active?.title ?? null}
          board={active}
          sessionId={sessionId}
        />
      </div>
    );
  }

  if (active === null) {
    return (
      <div
        data-testid="diagram-pane"
        data-state="empty"
        className="flex h-full min-h-0 flex-col"
      >
        {strip}
        {/* Round 3 (owner: "Where is my diagram? There should always be
            diagrams"): every answer draws one now, so an empty pane is a
            waiting room, not a resting state. It says so, quietly, in the
            greyed-out register of a placeholder rather than the confident
            voice of a finished screen. */}
        <div
          data-testid="diagram-pane-empty"
          data-reason={streaming ? 'drawing-in-progress' : 'nothing-drawn-yet'}
          className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-6 text-center opacity-70"
        >
          <SketchGlyph animate={streaming} />
          <p className="mt-0 mb-0 text-[length:var(--text-ui-size)] leading-[var(--text-ui-line)] text-tertiary">
            {streaming
              ? 'The guide is working. A picture usually lands partway through the answer.'
              : 'No diagram yet. The first answer draws the board here, and every answer after it edits it.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="diagram-pane" data-state="shown" className="flex h-full min-h-0 flex-col">
      {strip}

      {streaming ? (
        // A turn is running behind this picture: a newer drawing may replace
        // it any moment, and the pane says so instead of looking finished.
        <div
          data-testid="diagram-busy-note"
          className="mt-1 flex shrink-0 items-center gap-2 px-4 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary"
        >
          <span aria-hidden className="lm-thinking-pulse text-(--accent)">
            <span />
            <span />
            <span />
          </span>
          The guide is working; the board may change.
        </div>
      ) : null}

      {/* A drawing that throws while rendering degrades to its source here
          (2026-09-08), and the chat beside it never notices. Keyed on the
          drawing, like the body: the next drawing starts clean. */}
      <ErrorBoundary
        key={active.diagram_id}
        fallback={(error) => (
          <div
            data-testid="diagram-body-failed"
            className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto px-4 pt-3"
          >
            <p
              data-testid="diagram-title"
              className="mt-0 mb-0 truncate text-[length:var(--text-ui-size)] leading-[var(--text-ui-line)] font-medium text-primary"
            >
              {active.title}
            </p>
            <p className="mt-0 mb-0 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary">
              This drawing could not be shown ({errorMessageOf(error)}); here is its source.
            </p>
            <pre className="mono mt-0 mb-0 max-w-[72ch] overflow-x-auto rounded-card border border-default bg-inset p-3 whitespace-pre-wrap">
              {active.mermaid}
            </pre>
          </div>
        )}
      >
        <DiagramBody diagram={active} sessionId={sessionId} />
      </ErrorBoundary>
    </div>
  );
}

/**
 * The waiting room's mark (2026-09-06; owner: "I need a cooler
 * loading/thinking animation for the diagram section"): a small drawing
 * sketching itself. Three boxes and the two lines between them appear in
 * order, hold, fade, and start over; still, it is the finished sketch. The
 * motion is CSS (styles/index.css, `.lm-sketch`), so reduced-motion readers
 * get the still frame by construction.
 */
function SketchGlyph({ animate }: { animate: boolean }) {
  return (
    <svg
      width={96}
      height={56}
      viewBox="0 0 96 56"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="lm-sketch text-tertiary"
      data-animate={animate ? 'true' : 'false'}
      data-testid="diagram-sketch"
      aria-hidden
    >
      <rect className="lm-sketch-box" x="4" y="20" width="24" height="16" rx="4" pathLength="1" />
      <path className="lm-sketch-line" d="M28 28h16" pathLength="1" />
      <rect className="lm-sketch-box" x="44" y="4" width="24" height="16" rx="4" pathLength="1" />
      <rect className="lm-sketch-box" x="44" y="36" width="24" height="16" rx="4" pathLength="1" />
      <path className="lm-sketch-line" d="M44 28c6 0 6-16 12-16M44 28c6 0 6 16 12 16" pathLength="1" />
      <path className="lm-sketch-line" d="M68 12h8a4 4 0 0 1 4 4v20a4 4 0 0 1-4 4h-8" pathLength="1" />
    </svg>
  );
}

/**
 * One diagram: its title, the drawing, and whatever the reader has picked out
 * of it.
 *
 * Keyed on diagram_id by its parent, so switching diagrams resets the whole
 * local state (what is selected, which part is open, whether this one
 * rendered) instead of carrying a selection across two different pictures.
 * Pan and zoom, by contrast, leave a selection alone: framing the picture is
 * not changing your mind about it.
 *
 * MermaidDiagram owns both drawing outcomes (rendered SVG or the source in a
 * code block); the pane watches which one landed so a failure reads as a
 * failure instead of a wall of syntax.
 *
 * TWO PIECES OF STATE, because they answer different questions:
 *  - `selection` is the SET the reader has picked. Any number of parts, and
 *    what the round-6 scoped ask is about.
 *  - `openLabel` is the ONE part whose detail is on screen, which only a plain
 *    click or a plain Enter produces. A set of five nodes has no single shape
 *    or example to show, so building a set closes the detail panel.
 *
 * The modifier gestures are caught HERE, in the capture phase, rather than in
 * the hotspot decoration. The decoration reports a label and nothing else
 * (MermaidDiagram forwards it as a bare string, and that component belongs to
 * the chat feature), so a shift-click has to be recognised before it reaches
 * the element: a capture listener on the host runs first, takes the event,
 * and stops it, leaving the plain click to round 4's path untouched.
 */
const NO_SELECTION: readonly string[] = [];

/**
 * A dialog is up somewhere other than inside `within` (lib/ui Dialog, the
 * export dialog, the palette, the shapes key): Escape is its, and the picks
 * on the drawing wait. The part panels are dialogs too, inside the pane, and
 * do not count.
 */
function dialogOpenOutside(within: HTMLElement | null): boolean {
  return Array.from(document.querySelectorAll('[role="dialog"], [aria-modal="true"]')).some(
    (el) => within === null || !within.contains(el),
  );
}

function DiagramBody({ diagram, sessionId }: { diagram: SessionDiagram; sessionId: Ulid }) {
  const [failed, setFailed] = useState(false);
  // "Try again" remounts the drawing: a fresh instance draws from scratch.
  const [renderKey, setRenderKey] = useState(0);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // The picked parts live in the store alone (DECISIONS #88/#94): the depth
  // control reads them there, and this body reads and writes the same
  // field, keyed to this drawing, so nothing mirrors anything.
  const storedSelection = useChatStore((s) => s.diagramSelection);
  const setDiagramSelection = useChatStore((s) => s.setDiagramSelection);
  const requestComposer = useChatStore((s) => s.requestComposer);
  /** A part as the composer names it: an inline `@Label` token. */
  const tokenFor = useCallback(
    (label: string): MentionCandidate => ({
      key: `part:${label}`,
      kind: 'part',
      id: label,
      label,
      hint: diagram.parts.find((p) => p.label === label)?.what ?? null,
    }),
    [diagram.parts],
  );
  const selection: readonly string[] =
    storedSelection !== null &&
    storedSelection.diagramId === diagram.diagram_id &&
    storedSelection.sessionId === sessionId
      ? storedSelection.labels
      : NO_SELECTION;
  // The picked set is written twice on purpose: here at once (the marks on
  // the drawing must not wait for a round trip through the composer), and
  // by the composer when its text changes (DECISIONS #96: the `@Label`
  // tokens ARE the selection, so deleting one un-marks the part). Every
  // change here is also sent to the composer as a token to put in or take
  // out, which is what keeps the two the same.
  const setSelection = useCallback(
    (next: readonly string[] | ((prev: readonly string[]) => readonly string[])) => {
      const held = useChatStore.getState().diagramSelection;
      const prev: readonly string[] =
        held !== null && held.diagramId === diagram.diagram_id && held.sessionId === sessionId
          ? held.labels
          : NO_SELECTION;
      const labels = typeof next === 'function' ? next(prev) : next;
      setDiagramSelection(
        labels.length > 0 ? { sessionId, diagramId: diagram.diagram_id, labels: [...labels] } : null,
      );
      const added = labels.filter((l) => !prev.includes(l));
      const removed = prev.filter((l) => !labels.includes(l));
      // One request slot, one request: every gesture here either adds parts
      // (a toggle on, a marquee) or drops them (a toggle off, Escape), never
      // both, and a marquee's whole band rides a single insert so the
      // composer never consumes one token and forgets the rest.
      if (removed.length > 0) requestComposer({ kind: 'remove', labels: removed });
      if (added.length > 0) requestComposer({ kind: 'insert', candidates: added.map(tokenFor) });
    },
    [diagram.diagram_id, sessionId, setDiagramSelection, requestComposer, tokenFor],
  );
  // The picks leave with the pane.
  useEffect(() => () => setDiagramSelection(null), [setDiagramSelection]);
  const [openLabel, setOpenLabel] = useState<string | null>(null);
  /** Parts with nowhere to land in the drawing (see partHotspots). */
  const [stranded, setStranded] = useState<readonly DiagramPart[]>([]);
  /** Bumped every time the hotspots are (re)built, so the marks go back on. */
  const [decorated, setDecorated] = useState(0);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const parts = diagram.parts;

  const onPartsDecorated = useCallback((unmatched: readonly DiagramPart[]) => {
    // Only a real change may set state here: this runs inside the decorate
    // effect, and an unconditional set would loop.
    setStranded((prev) =>
      prev.length === unmatched.length && prev.every((p, i) => p.label === unmatched[i]?.label)
        ? prev
        : unmatched,
    );
    // This one is unconditional, and safe: it re-renders the pane but changes
    // nothing the decorate effect depends on, so nothing decorates again.
    setDecorated((n) => n + 1);
  }, []);

  /**
   * A plain click or Enter OPENS the part to read (2026-09-06; owner: "Just
   * cuz I click on a component doesn't mean I wanna add all of them to the
   * chat... It should be an explicit add"). Clicking another part opens that
   * one in its place; the canvas closes the dock. Nothing here touches the
   * question: the dock's own button does that.
   */
  const pick = useCallback((label: string) => setOpenLabel(label), []);

  /** The dock's button: this part in the question, or out of it, dock open. */
  const toggleInQuestion = useCallback(
    (label: string) => setSelection((prev) => orderedSelection(parts, toggleSelection(prev, label))),
    [parts, setSelection],
  );

  /** A modifier gesture: add or drop one part of the set without the dock. */
  const toggle = useCallback(
    (label: string) => {
      setOpenLabel(null);
      setSelection((prev) => orderedSelection(parts, toggleSelection(prev, label)));
    },
    [parts, setSelection],
  );

  /** The dock closes; the chips in the composer are the reader's to keep. */
  const closeDock = useCallback(() => {
    if (openLabel === null) return;
    focusPart(document, openLabel);
    setOpenLabel(null);
  }, [openLabel]);

  const clear = useCallback(() => {
    // Escape from anywhere: the dock, the chips, the marks on the drawing.
    if (openLabel === null && selection.length === 0) return;
    // Give the keyboard back what it came from, while it is still drawn.
    if (openLabel !== null) focusPart(document, openLabel);
    setSelection([]);
    setOpenLabel(null);
  }, [openLabel, selection.length, setSelection]);

  const anySelection = selection.length > 0 || openLabel !== null;

  // Shift (or cmd/ctrl) plus a click, Enter or Space toggles membership
  // without opening the dock. In the CAPTURE phase, so it runs before the
  // hotspot's own listener and can take the event away from it; without a
  // modifier it does nothing at all and the plain click picks (above).
  useEffect(() => {
    const host = hostRef.current;
    if (host === null) return;
    const onClick = (event: Event) => {
      const e = event as MouseEvent;
      if (!(e.shiftKey || e.metaKey || e.ctrlKey)) return;
      const label = partLabelAt(e.target);
      if (label === null) return;
      e.preventDefault();
      e.stopPropagation();
      toggle(label);
    };
    const onKeyDown = (event: Event) => {
      const e = event as KeyboardEvent;
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      if (!(e.shiftKey || e.metaKey || e.ctrlKey)) return;
      const label = partLabelAt(e.target);
      if (label === null) return;
      e.preventDefault();
      e.stopPropagation();
      toggle(label);
    };
    host.addEventListener('click', onClick, true);
    host.addEventListener('keydown', onKeyDown, true);
    return () => {
      host.removeEventListener('click', onClick, true);
      host.removeEventListener('keydown', onKeyDown, true);
    };
  }, [toggle]);

  // The set is drawn as a PASS over elements that already exist, never as a
  // re-decoration (partHotspots says why), and it is re-applied whenever the
  // drawing is rebuilt underneath it, which a theme change does.
  useEffect(() => {
    markSelectedParts(hostRef.current, selection);
  }, [selection, decorated]);

  // Escape clears EVERYTHING, from wherever the reader is (round 9, owner:
  // "if the user presses escape, it should deselect all, right?"): the
  // selection and the open panel. The key is shared, not taken (2026-09-08):
  // a handler that already answered it (the composer's @ picker) or a
  // dialog open over the page keeps its Escape, unless the reader is in the
  // pane itself, where the key can only mean this.
  useEffect(() => {
    if (!anySelection) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const body = bodyRef.current;
      const focusHere = body !== null && body.contains(document.activeElement);
      if (!focusHere && dialogOpenOutside(body)) return;
      clear();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [anySelection, clear]);

  /** A shift-drag finished: add everything the band crossed. */
  const onMarqueeSelect = useCallback(
    (rect: ScreenRect) => {
      const hit = partsInRect(hostRef.current, rect);
      if (hit.length === 0) return;
      setOpenLabel(null);
      setSelection((prev) => orderedSelection(parts, [...prev, ...hit]));
    },
    [parts, setSelection],
  );

  const open = parts.find((p) => p.label === openLabel) ?? null;
  // What the answer said about the open part, in its own words (2026-09-06).
  const answerText = useChatStore(
    (st) => newestAnswer(st.diagramSource[sessionId]?.messages ?? [])?.text ?? null,
  );
  const quotes = useMemo(
    () => (open !== null ? answerQuotes(answerText, open.label) : []),
    [answerText, open],
  );
  /** Only claim the drawing is selectable once it has hotspots to select. */
  const selectable = decorated > 0 && parts.length > stranded.length;

  return (
    <div ref={bodyRef} className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-2 px-4 pt-3">
        <p
          data-testid="diagram-title"
          className="mt-0 mb-0 min-w-0 flex-1 truncate text-[length:var(--text-ui-size)] leading-[var(--text-ui-line)] font-medium text-primary"
        >
          {diagram.title}
        </p>
      </div>
      {/* Parts the drawing gave nowhere to land used to appear here as a
          row of chips (every part, while mermaid was still rendering, or
          when a label's HTML escaping differed from the drawing's). The
          owner removed the row on 2026-09-07: a part is reachable through
          the drawing or not at all, and the label matching now unescapes
          entities on both sides. */}
      <div className="relative min-h-0 flex-1">
        <DiagramView
          resetKey={diagram.diagram_id}
          contentKey={decorated}
          onMarqueeSelect={onMarqueeSelect}
          onBackgroundClick={closeDock}
          status={
            selectable ? (
              // ONE element for both readings, and it never unmounts while the
              // drawing is up. That is what makes it a working live region: a
              // count that mounted fresh on each toggle would announce nothing,
              // and a reader building a selection by keyboard would hear
              // silence. Clearing says the hint again, which is the right thing
              // to say to someone who now has nothing picked.
              <p
                data-testid="selection-status"
                role="status"
                data-selecting={anySelection ? 'true' : 'false'}
                className="mt-0 mb-0 rounded-chip border border-default bg-raised px-2 py-0.5 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary"
              >
                {selection.length > 0 ? (
                  selectionCountLabel(selection.length)
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <PointerIcon />
                    {SELECT_HINT}
                  </span>
                )}
              </p>
            ) : null
          }
        >
          {/* Shrink-wrapped: the drawing's own size is what the fit measures. */}
          <div className="relative w-max" ref={hostRef}>
            {failed ? (
              <p data-testid="diagram-fallback-note" className="mb-1 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary">
                This diagram didn&rsquo;t render; showing its source.{' '}
                <button
                  type="button"
                  data-testid="diagram-retry"
                  className="cursor-pointer border-0 bg-transparent p-0 text-(--accent) underline-offset-2 hover:underline"
                  onClick={() => {
                    setFailed(false);
                    setRenderKey((k) => k + 1);
                  }}
                >
                  Try again
                </button>
              </p>
            ) : null}
            <MermaidDiagram
              key={renderKey}
              bare
              code={diagram.mermaid}
              parts={parts}
              selectedPart={openLabel}
              onSelectPart={pick}
              onPartsDecorated={onPartsDecorated}
              onState={(state) => setFailed(state === 'failed')}
            />
          </div>
        </DiagramView>
      </div>
      {open !== null ? (
        <PartPanel
          part={open}
          quotes={quotes}
          inQuestion={selection.includes(open.label)}
          onToggleQuestion={() => toggleInQuestion(open.label)}
          onClose={closeDock}
        />
      ) : null}
    </div>
  );
}
